import { useQuery } from '@tanstack/react-query';
import { tareaService } from '../services/tareaService';
import { CreateTareaForm, UpdateTareaForm, Tarea } from '../types';
import { useCrudResource } from './useCrudResource';

export const useTareas = () => {
  const crud = useCrudResource<Tarea, CreateTareaForm, UpdateTareaForm>({
    queryKey: ['tareas'],
    invalidateKeys: [['tareas-activas']],
    fetchList: async () => {
      const response = await tareaService.getTareas();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar las tareas');
      }
      return response.data || [];
    },
    create: async (tarea) => {
      const response = await tareaService.createTarea(tarea);
      if (!response.success) {
        throw new Error(response.message || 'Error al crear la tarea');
      }
      return response.data;
    },
    update: async (id, tarea) => {
      const response = await tareaService.updateTarea(id, tarea);
      if (!response.success) {
        throw new Error(response.message || 'Error al actualizar la tarea');
      }
      return response.data;
    },
    remove: async (id) => {
      const response = await tareaService.deleteTarea(id);
      if (!response.success) {
        // Lanza un error similar a Axios para conservar data.errors
        const err: any = new Error(response.message || 'Error al eliminar la tarea');
        err.response = { data: { message: response.message, errors: response.errors } };
        throw err;
      }
    },
  });

  const {
    data: tareasActivas,
    isLoading: isLoadingActivas,
    error: errorActivas,
    refetch: refetchTareasActivas,
  } = useQuery({
    queryKey: ['tareas-activas'],
    queryFn: async () => {
      const response = await tareaService.getTareasActivas();
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar las tareas activas');
      }
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  const updateTarea = (
    vars: { id: number; tarea: UpdateTareaForm },
    options?: Parameters<typeof crud.update>[1]
  ) => crud.update({ id: vars.id, data: vars.tarea }, options);

  return {
    tareas: crud.items,
    tareasActivas: tareasActivas || [],
    isLoading: crud.isLoading,
    isLoadingActivas,
    error: crud.error,
    errorActivas,
    createTarea: crud.create,
    updateTarea,
    deleteTarea: crud.removeAsync,
    isCreating: crud.isCreating,
    isUpdating: crud.isUpdating,
    isDeleting: crud.isRemoving,
    refetchTareas: crud.refetch,
    refetchTareasActivas,
    createError: crud.createError,
    updateError: crud.updateError,
    deleteError: crud.removeError,
  };
};
